import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, PhoneIcon, PhoneIncomingIcon, PhoneOutgoingIcon, PlayIcon, ClockIcon } from 'lucide-react';
import { PhoneCall } from '../types';
interface CallsModalProps {
  isOpen: boolean;
  onClose: () => void;
  calls: PhoneCall[];
  jobTitle: string;
  customerName: string;
}
export function CallsModal({
  isOpen,
  onClose,
  calls,
  jobTitle,
  customerName
}: CallsModalProps) {
  const [playingCallId, setPlayingCallId] = useState<string | null>(null);
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) {
      return `Today at ${date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit'
      })}`;
    } else if (diffDays === 1) {
      return `Yesterday at ${date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit'
      })}`;
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        hour: 'numeric',
        minute: '2-digit'
      });
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    }
  };
  const handlePlayCall = (callId: string) => {
    // Placeholder for audio playback
    setPlayingCallId(playingCallId === callId ? null : callId);
    console.log('Playing call:', callId);
  };
  return <AnimatePresence data-id="element-51">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40" data-id="element-52" />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4" data-id="element-53">
            <motion.div initial={{
          opacity: 0,
          y: 100,
          scale: 0.95
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 100,
          scale: 0.95
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col" data-id="element-54">
              {/* Header */}
              <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white p-6 flex items-center justify-between" data-id="element-55">
                <div className="flex-1" data-id="element-56">
                  <div className="flex items-center gap-2 mb-2" data-id="element-57">
                    <PhoneIcon className="w-5 h-5" data-id="element-58" />
                    <h2 className="text-xl font-bold" data-id="element-59">Phone Calls</h2>
                  </div>
                  <p className="text-slate-300 text-sm truncate" data-id="element-60">
                    {customerName}
                  </p>
                  <p className="text-slate-400 text-xs truncate" data-id="element-61">{jobTitle}</p>
                </div>
                <motion.button whileTap={{
              scale: 0.9
            }} onClick={onClose} className="p-2 hover:bg-slate-700 rounded-lg transition-colors" data-id="element-62">
                  <XIcon className="w-6 h-6" data-id="element-63" />
                </motion.button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6" data-id="element-64">
                {calls.length === 0 ? <div className="text-center py-12" data-id="element-65">
                    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-66">
                      <PhoneIcon className="w-8 h-8 text-slate-400" data-id="element-67" />
                    </div>
                    <p className="text-slate-600 font-semibold mb-1" data-id="element-68">
                      No calls yet
                    </p>
                    <p className="text-slate-500 text-sm" data-id="element-69">
                      Call history will appear here
                    </p>
                  </div> : <div className="space-y-3" data-id="element-70">
                    {calls.map((call, index) => <motion.div key={call.id} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: index * 0.05
              }} className="bg-slate-50 rounded-xl p-4 border-2 border-slate-100" data-id="element-71">
                        <div className="flex items-start justify-between mb-3" data-id="element-72">
                          <div className="flex items-start gap-3 flex-1" data-id="element-73">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${call.direction === 'inbound' ? 'bg-green-100' : 'bg-blue-100'}`} data-id="element-74">
                              {call.direction === 'inbound' ? <PhoneIncomingIcon className={`w-5 h-5 ${call.direction === 'inbound' ? 'text-green-600' : 'text-blue-600'}`} data-id="element-75" /> : <PhoneOutgoingIcon className={`w-5 h-5 ${call.direction === 'inbound' ? 'text-green-600' : 'text-blue-600'}`} data-id="element-76" />}
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-77">
                              <p className="font-bold text-slate-900 capitalize mb-1" data-id="element-78">
                                {call.direction} Call
                              </p>
                              <p className="text-sm text-slate-500 mb-1" data-id="element-79">
                                {formatDate(call.date)}
                              </p>
                              <div className="flex items-center gap-1.5 text-xs text-slate-600" data-id="element-80">
                                <ClockIcon className="w-3.5 h-3.5" data-id="element-81" />
                                <span data-id="element-82">{formatDuration(call.duration)}</span>
                              </div>
                              {call.notes && <p className="text-sm text-slate-600 mt-2 italic" data-id="element-83">
                                  "{call.notes}"
                                </p>}
                            </div>
                          </div>
                        </div>

                        {call.recordingUrl && <motion.button whileTap={{
                  scale: 0.95
                }} onClick={() => handlePlayCall(call.id)} className={`w-full px-4 py-2.5 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 transition-all ${playingCallId === call.id ? 'bg-orange-600 text-white' : 'bg-white border-2 border-slate-200 text-slate-700 hover:border-orange-300'}`} data-id="element-84">
                            <PlayIcon className="w-4 h-4" data-id="element-85" />
                            {playingCallId === call.id ? 'Playing...' : 'Listen to Recording'}
                          </motion.button>}

                        {!call.recordingUrl && <div className="w-full px-4 py-2.5 bg-slate-100 rounded-lg text-center" data-id="element-86">
                            <p className="text-xs text-slate-500" data-id="element-87">
                              No recording available
                            </p>
                          </div>}
                      </motion.div>)}
                  </div>}
              </div>

              {/* Footer */}
              {calls.length > 0 && <div className="border-t-2 border-slate-100 p-4 bg-slate-50" data-id="element-88">
                  <div className="flex items-center justify-between text-sm" data-id="element-89">
                    <span className="text-slate-600" data-id="element-90">Total Calls</span>
                    <span className="font-bold text-slate-900" data-id="element-91">
                      {calls.length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-1" data-id="element-92">
                    <span className="text-slate-600" data-id="element-93">Total Duration</span>
                    <span className="font-bold text-slate-900" data-id="element-94">
                      {formatDuration(calls.reduce((sum, call) => sum + call.duration, 0))}
                    </span>
                  </div>
                </div>}
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}